<script lang="ts" setup>
import { computed } from 'vue'
import { POP_TYPE, VIP_TYPE } from '@/constants'
import { brand } from '@/config/brands'
import { useI18n } from '@/composables/useI18n'
import type { MemberTopupSubscribeProduct } from '@/composables/payment'
import { getDailyPrice, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '@/composables/payment/utils/subscribeDailyPrice'

const props = defineProps<{
  value: boolean
  product: MemberTopupSubscribeProduct | null
  countdown: number
  countdownText: string
  showCloseButton: boolean
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'close'): void
  (e: 'action', product: MemberTopupSubscribeProduct | null): void
}>()

const { t } = useI18n()

const bgImage = computed(() => `/images/${brand}/bg_retention_popup.png`)
const flameImage = computed(() => `/images/${brand}/tier_flame.png`)
const lineLeftImage = computed(() => `/images/${brand}/countdown_line_left.png`)
const lineRightImage = computed(() => `/images/${brand}/countdown_line_right.png`)
const fingerPointerImage = computed(() => `/images/${brand}/finger_pointer.png`)

function isZero(value: unknown) {
  // 与 useSubscription.isZero 保持一致：未配置优惠价不等同于 0 元优惠价。
  if (value === null || value === undefined || value === '')
    return false
  return Number.parseFloat(String(value)) === 0
}

// 与订阅档位卡复用同一套展示规则：优惠日均价、划线原价及优惠总价均由商品数据决定。
const priceDisplay = computed(() => {
  const product = props.product
  if (!product)
    return ''
  if (product.subscribeType === VIP_TYPE.DISCOUNT)
    return `$${product.preferentialPrice || product.price}`
  if (isZero(product.preferentialPrice))
    return t('subscriptionFree')
  if (!product.preferentialPrice)
    return `$${getUnitPriceValue(product) || product.price}`
  return `$${shouldShowDailyPrice(product, isZero) ? getDailyPrice(product) : product.preferentialPrice}`
})

const showDailyPrice = computed(() => !!props.product && (
  (!props.product.preferentialPrice && !!getUnitPriceValue(props.product))
  || shouldShowDailyPrice(props.product, isZero)
))

const originalPrice = computed(() => {
  const product = props.product
  if (!product)
    return ''
  if (product.subscribeType === VIP_TYPE.DISCOUNT)
    return product.discountOriginalPrice ? `$${product.discountOriginalPrice}` : ''
  if (isZero(product.preferentialPrice) || shouldShowDailyPrice(product, isZero))
    return `$${product.price}`
  return ''
})

const preferentialTotal = computed(() => {
  const product = props.product
  if (!product || !shouldShowPreferentialTotal(product, isZero))
    return ''
  return `$${product.preferentialPrice}`
})

const countdownParts = computed(() => {
  const total = Math.max(0, props.countdown)
  const min = Math.floor(total / 60)
  const sec = total % 60
  return {
    min: String(min).padStart(2, '0'),
    sec: String(sec).padStart(2, '0'),
  }
})

function handleAction() {
  emit('action', props.product)
}

function handleClose() {
  emit('close')
  emit('input', false)
}

const cycleDays = computed(() => {
  if (!props.product)
    return '7'
  return props.product.cycleStr?.replace(/\D/g, '') || '7'
})
</script>

<template>
  <BasicPopup :value="value" :pop-type="POP_TYPE.CENTER" :center-z-index="101" :closable="false" :mask="true" @input="v => emit('input', v)">
    <div v-if="value" class="retention-popup" @click="handleAction">
      <img :src="bgImage" class="retention-bg" alt="">

      <div class="retention-overlay">
        <div class="retention-subtitle">
          <p class="subtitle-strong">
            {{ t('retention.luckyWinner') }}
          </p>
          <p class="subtitle-normal">
            {{ t('retention.lowPriceAccess') }}
          </p>
        </div>

        <div v-if="product" class="tier-card">
          <span class="popular-badge">{{ t('retention.popularTag') }}</span>
          <img :src="flameImage" class="tier-flame" alt="">

          <div class="tier-row tier-row-main">
            <span class="tier-name">{{ product?.name || '' }}</span>
            <span class="tier-price">
              {{ priceDisplay }}<span v-if="showDailyPrice" class="daily-price-unit"> / Day</span>
            </span>
          </div>
          <div class="tier-row tier-row-sub">
            <span class="tier-desc">{{ t('retention.readAllStories', { 0: cycleDays }) }}</span>
            <span class="tier-price-detail">
              <span v-if="originalPrice" class="original-price">{{ originalPrice }}</span>
              <span v-if="preferentialTotal" class="preferential-total">{{ preferentialTotal }}</span>
            </span>
          </div>
        </div>

        <div class="retention-countdown">
          <img :src="lineLeftImage" class="countdown-line" alt="">
          <span class="cd-label">{{ t('retention.expirePrefix') }}</span>
          <span class="cd-box">{{ countdownParts.min }}</span>
          <span class="cd-colon">:</span>
          <span class="cd-box">{{ countdownParts.sec }}</span>
          <img :src="lineRightImage" class="countdown-line" alt="">
        </div>

        <div class="cta-wrap">
          <button class="cta-button">
            {{ t('retention.ctaButton') }}
          </button>
          <img :src="fingerPointerImage" class="finger-pointer" alt="">
        </div>
      </div>

      <div v-show="showCloseButton" class="close-btn" @click.stop="handleClose">
        ✕
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.retention-popup {
  --retention-gradient: linear-gradient(-90deg, rgba(235, 92, 113, 1) 0%, rgba(235, 92, 113, 1) 0%, rgba(247, 165, 89, 1) 100%, rgba(247, 165, 89, 1) 100%);

  position: relative;
  width: 68.9147286822vw;
  max-width: 258.43px;
}

.retention-bg {
  width: 100%;
  display: block;
}

/* 叠加层精确覆盖在背景图的浅色卡片区域内（标题下方至底部留白之间） */
.retention-overlay {
  position: absolute;
  top: 25%;
  left: 4.4994375703%;
  right: 4.4994375703%;
  bottom: 4.84%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.retention-subtitle {
  text-align: center;
  margin-bottom: calc(6.427688504% + 4px);
  transform: translateY(4px);
}

.subtitle-strong {
  font-size: 10.46px;
  font-weight: 500;
  line-height: 9.88px;
  color: rgba(129, 45, 29, 1);
  margin: 0;
}

.subtitle-normal {
  font-size: 10.46px;
  font-weight: 500;
  line-height: 9.88px;
  color: rgba(129, 45, 29, 1);
  margin: 4px 0 0;
}

.tier-card {
  position: relative;
  width: 100%;
  aspect-ratio: 809 / 239;
  box-sizing: border-box;
  background: var(--retention-gradient);
  border-radius: 9.59px;
  padding: 0 11.63px;
  overflow: hidden;
  cursor: pointer;
}

.popular-badge {
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(248, 218, 131, 1.0);
  color: rgba(151, 58, 22, 1);
  box-sizing: border-box;
  width: 43.58px;
  height: 12.78px;
  padding: 2.91px 0 0 5.81px;
  border-radius: 9.59px 0 9.59px 0;
  font-size: 8.72px;
  font-weight: 700;
  line-height: 8.14px;
  z-index: 1;
}

.tier-flame {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 55.23px;
  opacity: 0.5;
  pointer-events: none;
}

.tier-row {
  position: absolute;
  left: 11.63px;
  right: 11.63px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tier-row-main {
  top: 19.18px;
  margin: 0;
  align-items: flex-start;
}

.tier-name {
  margin-top: 1.16px;
  font-size: 17.44px;
  font-weight: 800;
  line-height: 15.69px;
  color: #fff;
}

.tier-price {
  font-size: 18.59px;
  font-weight: 800;
  line-height: 17.14px;
  color: #fff;
}

.daily-price-unit {
  font-size: 9.88px;
  font-weight: 400;
}

.tier-row-sub {
  top: 42.1px;
  margin: 0;
  align-items: flex-start;
}

.tier-desc {
  margin-top: 0.33px;
  font-size: 9.88px;
  line-height: 9.3px;
  color: rgba(255, 238, 179, 1);
}

.original-price {
  font-size: 9.88px;
  line-height: 9.01px;
  color: rgba(255, 255, 255, 1);
  text-decoration: line-through;
}

.tier-price-detail {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  min-height: 9.01px;
}

.preferential-total {
  font-size: 9.88px;
  font-weight: 500;
  line-height: 9.01px;
  color: #fff;
}

.retention-countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  margin: 5.9332509271% 0;
}

.countdown-line {
  height: 2.91px;
  flex: 1;
  max-width: 48px;
  object-fit: contain;
}

.cd-label {
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(153, 48, 69, 1);
  white-space: nowrap;
}

.cd-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18.02px;
  height: 18.02px;
  min-width: 0;
  padding: 0;
  border-radius: 4.07px;
  background: rgba(245, 214, 202, 1.0);
  color: rgba(153, 48, 69, 1);
  font-size: 10.5px;
  font-weight: 700;
  text-align: center;
}

.cd-colon {
  color: rgba(153, 48, 69, 1);
  font-size: 10.5px;
  font-weight: 700;
}

.cta-wrap {
  position: absolute;
  right: 0;
  bottom: 53.5px;
  left: 0;
  width: 100%;
}

.cta-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 809 / 138;
  height: auto;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: var(--retention-gradient);
  color: #fff;
  font-size: 14.53px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;
}

.finger-pointer {
  position: absolute;
  top: 55.7971014493%;
  left: 76.6378244747%;
  width: 33.9925834363%;
  z-index: 2;
  pointer-events: none;
  animation: finger-bounce 1.5s ease-in-out infinite;
}

@keyframes finger-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.close-btn {
  position: absolute;
  top: -10.5%;
  right: -2.5%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  background: transparent;
  cursor: pointer;
  z-index: 10;
}
</style>
